/**
 * RichTextContent Component
 *
 * Reusable component for rendering Contentful Rich Text fields.
 * Converts Rich Text JSON to HTML and renders via Markup component.
 * Supports embedded entries and assets via bridge components.
 *
 * Usage:
 *   import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
 *
 *   <RichTextContent content={component.blogContent} />
 *   <RichTextContent content={component.description} className="custom-class" />
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {BLOCKS} from '@contentful/rich-text-types';
import {documentToHtmlString} from '@contentful/rich-text-html-renderer';

import {Markup} from '@amblique/base/app/components/shared/markup';

/**
 * Build lookup maps from Rich Text links
 *
 * @param {Object} links - Rich Text links {entries, assets}
 * @returns {Object} {entryMap, assetMap}
 */
const buildLinkMaps = (links) => {
  const entryMap = new Map();
  const assetMap = new Map();

  // Map entries by ID
  links?.entries?.block?.forEach((entry) => {
    if (entry?.sys?.id) {
      entryMap.set(entry.sys.id, entry);
    }
  });

  // Map assets by ID
  links?.assets?.block?.forEach((asset) => {
    if (asset?.sys?.id) {
      assetMap.set(asset.sys.id, asset);
    }
  });

  return {entryMap, assetMap};
};

/**
 * Escape HTML attribute value
 *
 * @param {string} str - String to escape
 * @returns {string}
 */
const escapeAttr = (str) => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

/**
 * Create render options for documentToHtmlString with embedded entry/asset support
 *
 * @param {Map} entryMap - Entry lookup map
 * @param {Map} assetMap - Asset lookup map
 * @returns {Object} Render options
 */
const createRenderOptions = (entryMap, assetMap) => ({
  renderNode: {
    // Embedded Entry Block
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const entryId = node?.data?.target?.sys?.id;
      const entry = entryMap.get(entryId);

      if (!entry) {
        console.warn(`[RichTextContent] Embedded entry not found: ${entryId}`);

        return '';
      }

      // Render as placeholder div with component data
      const componentJson = escapeAttr(JSON.stringify(entry));

      return `<div role="embedded-entry-block" data-id="${entryId}" data-component="${componentJson}"></div>`;
    },

    // Embedded Asset Block
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const assetId = node?.data?.target?.sys?.id;
      const asset = assetMap.get(assetId);

      if (!asset) {
        console.warn(`[RichTextContent] Embedded asset not found: ${assetId}`);

        return '';
      }

      // Render as placeholder div with asset data
      const assetJson = escapeAttr(JSON.stringify(asset));

      return `<div role="embedded-asset-block" data-id="${assetId}" data-asset="${assetJson}"></div>`;
    },
  },
});

/**
 * Convert Contentful Rich Text to HTML string with embedded entry/asset support
 *
 * Embedded entries are rendered as: <div role="embedded-entry-block" data-id="..." data-component="..."></div>
 * Embedded assets are rendered as: <div role="embedded-asset-block" data-id="..." data-asset="..."></div>
 *
 * These placeholders are then converted to React components by jsxParser via Markup component.
 *
 * @param {Object} richTextField - Rich text field from Contentful {json, links}
 * @returns {string|null}
 */
export const richTextToHtml = (richTextField) => {
  if (!richTextField?.json) return null;

  const {json, links} = richTextField;

  // If no links, use simple conversion
  if (!links) {
    return documentToHtmlString(json);
  }

  // Build lookup maps and create render options
  const {entryMap, assetMap} = buildLinkMaps(links);
  const options = createRenderOptions(entryMap, assetMap);

  return documentToHtmlString(json, options);
};

/**
 * RichTextContent Component
 *
 * Renders Contentful Rich Text field as HTML via Markup component.
 * Supports embedded entries and assets via bridge components in jsxParser.
 *
 * @param {Object} props - Component props
 * @param {Object} props.content - Rich text field from Contentful {json, links}
 * @param {React.ElementType} [props.as] - Element type to render as
 * @param {string} [props.className] - Additional CSS classes
 * @returns {React.ReactElement|null}
 */
export const RichTextContent = ({content, as, className = '', ...props}) => {
  const html = useMemo(() => richTextToHtml(content), [content]);

  if (!html) return null;

  return (
    <Markup
      as={as}
      className={`rich-text-content ${className}`.trim()}
      source={html}
      {...props}
    />
  );
};

RichTextContent.displayName = 'RichTextContent';
RichTextContent.propTypes = {
  content: PropTypes.shape({
    json: PropTypes.object,
    links: PropTypes.shape({
      entries: PropTypes.shape({
        block: PropTypes.array,
      }),
      assets: PropTypes.shape({
        block: PropTypes.array,
      }),
    }),
  }),
  as: PropTypes.elementType,
  className: PropTypes.string,
};

export default RichTextContent;
